import { EligibleProfessional } from "@/domain/entities/EligibleProfessional"
import { Professional } from "@/domain/entities/Professional"
import { EProfessionalApprovalStatus } from "@/domain/enums/EProfessionalApprovalStatus"
import { EProfessionalRejectionStep } from "@/domain/enums/EProfessionalRejectionStep"
import { EligibleProfessionalResponseDataDTO } from "../dto/EligibleProfessionalListResponseDTO"
import { ProfessionalDetailResponseDataDTO } from "../dto/ProfessionalDetailResponseDTO"
import WorkingHourDataMappers from "./WorkingHourDataMappers"

// GET admin/professionals/{userId} devolve um composto { user, detail,
// crms, verification } — o Professional do domínio usa detail + verification
// (crms é usado só pra extrair a carteira profissional abaixo; a listagem
// completa de CRMs é coberta em outro lugar da tela, por UserCredentialList).
const toDomain = (data: ProfessionalDetailResponseDataDTO): Professional | null => {
    // Carteira exibida é a do CRM usado pra atender (for_appointments), não
    // mais do favorito — sem fallback: se nenhum CRM for for_appointments,
    // não tem carteira pra mostrar mesmo.
    if (!data.detail) return null
    const appointmentsCrm = data.crms?.find((crm) => crm.for_appointments) || null;

    return {
        id: data.detail.id,
        user_id: data.detail.user_id,
        photo_url: data.detail.photo_url,
        gender: data.detail.gender,
        rqe: data.detail.rqe,
        bio: data.detail.bio,
        education: data.detail.education,
        experience: data.detail.experience,
        graduation_institution: data.detail.graduation_institution,
        graduation_year: data.detail.graduation_year,
        residency: data.detail.residency,
        other_education: data.detail.other_education,
        current_practice_location: data.detail.current_practice_location,
        practicing_since: data.detail.practicing_since,
        uf: data.detail.uf,
        specialty_id: data.detail.specialty_id,
        specialty_name: data.detail.specialty?.name || null,
        price: data.detail.price,
        accept_appointments: data.detail.accept_appointments,
        approved: data.detail.approved as EProfessionalApprovalStatus,
        rejection_step: (data.detail.rejection_step as EProfessionalRejectionStep) || null,
        rejection_reason: data.detail.rejection_reason || null,
        // já vêm como URL completa (official_document_photo_url etc.) — não
        // precisa mais prefixar com VUE_APP_IMG_BASE_URL no presentation mapper.
        official_document_photo: data.verification?.official_document_photo_url || null,
        specialist_certificate: data.verification?.specialist_certificate_url || null,
        selfie_photo: data.verification?.selfie_photo_url || null,
        submitted_at: data.verification?.submitted_at || null,
        professional_card_front: appointmentsCrm?.document_front_url || null,
        professional_card_back: appointmentsCrm?.document_back_url || null,
        professional_card_number: appointmentsCrm?.number || null,
        professional_card_uf: appointmentsCrm?.uf || null,
        professional_card_document_type: appointmentsCrm?.document_type || null,
        professional_card_valid: appointmentsCrm ? appointmentsCrm.valid : null,
        is_eligible: !!data.verification,
        working_hours: WorkingHourDataMappers.toEntityList(data.working_hours),
    }
}

// Item da lista GET admin/professionals?status= — bem mais enxuto que o
// Professional completo (usado só na lista + link pro detalhe do usuário).
const toEligibleDomain = (data: EligibleProfessionalResponseDataDTO): EligibleProfessional => ({
    user_id: data.user_id,
    name: data.name,
    approved: data.approved as EProfessionalApprovalStatus,
    rejection_step: (data.rejection_step as EProfessionalRejectionStep) || null,
})

const toEligibleDomainList = (data: EligibleProfessionalResponseDataDTO[]): EligibleProfessional[] =>
    (data || []).map(toEligibleDomain)

export default { toDomain, toEligibleDomain, toEligibleDomainList }
